/**
 * Which neighbour the D-pad should land on — as arithmetic over rectangles,
 * with no DOM and no React in it.
 *
 * It lives apart from Focusable.tsx for one reason: this is the part that was
 * WRONG, and a pure function over plain rectangles can be pinned by a test
 * that runs in Node. The bug it fixes is not visible in code review — every
 * version of this scoring looks reasonable — and it is tedious to reproduce by
 * hand on a television, which is the combination that means "write the test".
 */

export interface Rect { left: number; top: number; right: number; bottom: number; }

export type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * THE RULE THAT MATTERS: a candidate sharing the lane beats one that does not,
 * always — not by a weighting, by a bucket.
 *
 * The first version scored `forward + offAxis * 3` across every candidate at
 * once and took the minimum, which means a big enough gap along the row can
 * always be beaten by something closer on a different row. That is exactly
 * what "the navigation jumps around" was: walking LEFT along the filter chips,
 * the next chip sat a hundred-odd pixels away while the toolbar sat fifty
 * pixels up, so focus left the row and landed in the toolbar. A heavier
 * penalty can only make that rarer; it cannot make it not happen, because
 * there is always a layout where the numbers cross over.
 *
 * So: candidates OVERLAPPING the source on the cross axis — same row for
 * left/right, same column for up/down — are considered first, and the
 * off-axis term only breaks ties among them. Nothing outside the lane is
 * looked at unless the lane is empty.
 *
 * Distances are edge to edge, not centre to centre. A wide element beside a
 * narrow one has a centre that reads as "behind you" while its body is plainly
 * to the right, and centre distance also makes a long chip feel further away
 * than a short one at the same gap.
 *
 * LEFT and RIGHT never leave the lane at all. Vertical travel does fall back
 * to the nearest unaligned candidate, because moving between a toolbar, a
 * caption and a grid is the whole point of up and down — but running sideways
 * off the end of a row should stop, the way it does on a set-top box, rather
 * than teleporting somewhere the eye did not follow.
 *
 * Returns the INDEX of the winner, or -1. An index rather than the rect so the
 * caller can keep whatever it is that owns these rectangles.
 *
 * `self` is the index of `from` within `rects`, or -1 when the starting point
 * is not among the candidates — which is a real case, not a defensive one: the
 * player chrome fades out while focus sits on one of its buttons, and that
 * button must stay a starting point after it stops being a landing spot.
 *
 * `inside` marks candidates that scroll with the starting point — see the
 * SCROLLED-OUT CONTENT note below. Omit it and everything is treated as
 * sharing one surface, which is correct for a screen that does not scroll.
 */
export function nearestInDirection(
  from: Rect, rects: Rect[], dir: Direction, self = -1, inside?: boolean[],
  zones?: number[], anchorX?: number,
): number {
  const horizontal = dir === 'left' || dir === 'right';

  /*
   * THE COLUMN YOU STARTED IN, remembered across vertical moves.
   *
   * Spatial navigation is otherwise stateless: each press scores from
   * whatever is focused right now. That is fine when every row has the same
   * item width, and wrong the moment they do not — Continue Watching is
   * 16:9 stills, the rows below are 2:3 posters, so their centres do not line
   * up. Go down from the first poster and you land correctly; come back up
   * and the nearest CENTRE is the second still, because it happens to overlap
   * where the poster's centre was. You drift a column per row, and the drift
   * is invisible until you try to go back the way you came.
   *
   * So a vertical move scores against the x you were at when the vertical
   * movement STARTED, not against the current element. A horizontal move
   * sets a new one. This is what every television interface does, and the
   * reason is exactly this: rows are not all the same shape.
   */
  const originX = !horizontal && anchorX !== undefined
    ? anchorX
    : (from.left + from.right) / 2;

  /*
   * ZONES COME FIRST, AHEAD OF EVERYTHING ELSE.
   *
   * An app shell is three bands: chrome along the top, content, chrome along
   * the bottom. The surface rule below cannot see that, because the top nav
   * and the bottom player both sit outside the scrolling area and therefore
   * "share a surface" with each other — so pressing DOWN from the library
   * tabs preferred the now-playing bar at the bottom of the screen over the
   * posters directly beneath the cursor. It had to cross the entire content
   * area to do it, and it did, because arithmetic about surfaces beat
   * arithmetic about distance.
   *
   * The fix is to say the obvious thing out loud: moving down from the top
   * band enters the content, and only leaves for the bottom band when there
   * is no content left to reach. Left and right never change band at all.
   *
   * 0 = top chrome, 1 = content, 2 = bottom chrome. Absent means content,
   * which is why nothing had to be labelled except the chrome itself.
   */
  const myZone = zones && self >= 0 ? zones[self] ?? 1 : 1;

  /*
   * SCROLLED-OUT CONTENT IS NOT FAR AWAY, IT IS JUST SCROLLED.
   *
   * Rects are viewport coordinates, so a row that has scrolled off the top
   * sits at a NEGATIVE y — and the further you scroll, the further away it
   * measures. A navigation bar pinned at the top of the window does not move,
   * so past a certain scroll depth the nav is arithmetically nearer than the
   * row immediately above, and pressing UP jumps to the menu instead of
   * scrolling back. That is what it does: on the library's third row, UP left
   * the content entirely.
   *
   * Distance cannot answer this, because the two things are measured on
   * different surfaces. So candidates that scroll WITH you are preferred over
   * ones that do not, and only when there are none of the former does
   * anything pinned become a target — which is also exactly the behaviour you
   * want: walk up through the content, and leave for the chrome from the top.
   */
  let aligned = -1, alignedScore = Infinity;
  let loose = -1, looseScore = Infinity;
  let outAligned = -1, outAlignedScore = Infinity;
  let outLoose = -1, outLooseScore = Infinity;
  /** Candidates in a band beyond this one, considered only if nothing nearer. */
  const farther: number[] = [];

  for (let i = 0; i < rects.length; i++) {
    if (i === self) continue;
    const b = rects[i];

    // Gap between the facing edges. Negative means overlapping, or behind.
    const forward = dir === 'right' ? b.left - from.right
                  : dir === 'left'  ? from.left - b.right
                  : dir === 'down'  ? b.top - from.bottom
                  :                   from.top - b.bottom;
    // -1 rather than 0: adjacent elements often share a boundary pixel, and
    // half a pixel of rounding should not make a neighbour invisible.
    if (forward < -1) continue;

    const overlap = horizontal
      ? Math.min(from.bottom, b.bottom) - Math.max(from.top, b.top)
      : Math.min(from.right, b.right) - Math.max(from.left, b.left);

    const offAxis = horizontal
      ? Math.abs((b.top + b.bottom) / 2 - (from.top + from.bottom) / 2)
      : Math.abs((b.left + b.right) / 2 - originX);

    /*
     * COMING DOWN OUT OF THE CHROME, ENTER AT THE LEFT.
     *
     * Everywhere else "nearest" is what a person means: from row five, down
     * goes to the poster under the one you are on. But the top chrome is a
     * horizontal strip of links and the content below it is a grid, and there
     * is no column under "Live TV" in any meaningful sense — the nearest
     * poster is wherever that word happens to sit, which is the third column
     * on one screen and the second on another.
     *
     * A grid is entered at its start. That is what somebody means by coming
     * down the left-hand side, it is the same place every time, and from
     * there moving right is one press and obvious. Using the candidate's LEFT
     * EDGE rather than its centre-distance does exactly that, while `forward`
     * still decides which ROW is entered.
     *
     * ANY downward move OUT OF THE TOP CHROME, not just the one into the
     * grid. The navigation and the library tabs are BOTH top chrome, so a
     * rule written as "chrome to content" skipped the first step entirely:
     * down from Live TV landed on Wrestling — whichever tab happened to sit
     * under that word — and only then went to the tiles. Two hops, each
     * landing somewhere arbitrary.
     *
     * The chrome is rows of words. Leaving any of them downward enters the
     * next row at its start.
     *
     * Within the content, nearest wins as it always has: there the column
     * under the cursor is a real thing and following it is what people mean.
     */
    const enteringContent = !horizontal && dir === 'down' && zones && myZone === 0;

    const score = enteringContent
      ? Math.max(forward, 0) + b.left
      : Math.max(forward, 0) + offAxis;

    /*
     * A band you are not travelling towards is not a candidate. Moving down
     * cannot reach the top chrome, moving up cannot reach the bottom bar, and
     * sideways stays where it is — each of which is what a person means, and
     * none of which distance alone would produce.
     */
    if (zones) {
      const zone = zones[i] ?? 1;
      if (horizontal) { if (zone !== myZone) continue; }
      else if (dir === 'down') { if (zone < myZone) continue; }
      else if (zone > myZone) continue;

      /*
       * Within reach of the cursor's own band first. Anything further out is
       * only considered once nothing nearer answered — see the second pass
       * below, which is what makes "down from the tabs, through the posters,
       * and eventually to the player" walk in that order rather than jumping.
       */
      if (zone !== myZone) { farther.push(i); continue; }
    }

    // No `inside` array means one surface, so everything counts as sharing it.
    const shares = inside ? inside[i] !== false : true;

    /*
     * LANES DO NOT APPLY WHEN ENTERING FROM THE CHROME, and this is the part
     * that made the previous attempt do nothing.
     *
     * The buckets below are absolute: `if (aligned >= 0) return aligned` hands
     * back the candidate that OVERLAPS the cursor horizontally, whatever it
     * scored. That is right almost everywhere — the thing directly beneath you
     * is what you meant. But coming down from a row of words it means the tile
     * that happens to sit under "Live TV", and no amount of scoring the
     * leftmost tile better could win, because it was in a bucket that is only
     * consulted when the first one is empty.
     *
     * So for this one crossing every candidate goes in the same bucket and
     * they compete on score alone — which is `forward + left edge`: nearest
     * row, then leftmost in it.
     */
    const lane = enteringContent ? 1 : overlap;

    if (shares) {
      if (lane > 0) {
        if (score < alignedScore) { alignedScore = score; aligned = i; }
      } else if (score < looseScore) { looseScore = score; loose = i; }
    } else if (lane > 0) {
      if (score < outAlignedScore) { outAlignedScore = score; outAligned = i; }
    } else if (score < outLooseScore) { outLooseScore = score; outLoose = i; }
  }

  // Four buckets, in order: sharing the surface beats not sharing it, and
  // within each, sharing the lane beats not sharing it.
  if (aligned >= 0) return aligned;
  if (!horizontal && loose >= 0) return loose;
  if (outAligned >= 0) return outAligned;
  if (!horizontal && outLoose >= 0) return outLoose;

  /*
   * Nothing in this band. NOW the next one is fair game — which is how you
   * leave the content for the player at the bottom, having walked through the
   * content rather than over it.
   *
   * Re-scored without the zone filter, and only among the ones set aside, so
   * this is the same nearest-in-direction question asked of a smaller set.
   */
  if (horizontal || farther.length === 0) return -1;
  let best = -1, bestScore = Infinity;
  let bestZone = Infinity;
  for (const i of farther) {
    const b = rects[i];
    const forward = dir === 'down' ? b.top - from.bottom : from.top - b.bottom;
    const offAxis = Math.abs((b.left + b.right) / 2 - originX);
    /*
     * DISTANCE IN BANDS, not the band's number.
     *
     * Preferring the lowest number is only right going down. Coming UP from
     * the player at the bottom it reached past the content and landed on the
     * tabs at the very top — the same skip-the-middle bug this whole change
     * exists to fix, mirrored. The filter above has already discarded the
     * bands behind us, so what is left is simply "how many bands away".
     */
    const zone = zones ? zones[i] ?? 1 : 1;
    const bands = Math.abs(zone - myZone);
    const score = Math.max(forward, 0) + offAxis;
    if (bands < bestZone || (bands === bestZone && score < bestScore)) {
      bestZone = bands; bestScore = score; best = i;
    }
  }
  return best;
}
